import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getStudents } from "../../services/students";
import { StudentWithUserInfo } from "../../types/student";
import LoadingSpinner from "../common/LoadingSpinner";
import StudentDetailsModal from "../modals/StudentDetailsModal";
import StudentModal from "../forms/StudentModal";
import { Eye, Edit, Trash2 } from "lucide-react";

const StudentsTable: React.FC = () => {
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(
    null
  );
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {
    data: studentsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["students"],
    queryFn: () => getStudents({}),
    refetchOnWindowFocus: false,
  });

  const students = studentsResponse?.data || [];

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    console.error("StudentsTable - Error:", error);
    return (
      <div className="text-red-500 text-center p-4">
        Error loading students:{" "}
        {error instanceof Error ? error.message : "Unknown error"}
      </div>
    );
  }

  if (!studentsResponse?.success) {
    console.warn("StudentsTable - API call not successful:", studentsResponse);
  }

  const handleViewDetails = (studentId: string) => {
    setSelectedStudentId(studentId);
    setIsDetailsModalOpen(true);
  };

  const handleEdit = (studentId: string) => {
    setSelectedStudentId(studentId);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (studentId: string) => {
    if (window.confirm("Are you sure you want to delete this student?")) {
      // TODO: Implement delete functionality
    }
  };

  return (
    <>
      <div className="overflow-x-auto bg-white rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1 border-0">
        <table className="min-w-full">
          <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Admission Number
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Class
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {students.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No students found
                </td>
              </tr>
            ) : (
              students.map((student: StudentWithUserInfo) => (
                <tr key={student._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {student.admissionNumber}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {student.userInfo?.firstName} {student.userInfo?.lastName}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.userInfo?.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {student.classInfo?.[0]?.name || "Not assigned"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        student.academicStatus === "active"
                          ? "bg-green-100 text-green-800"
                          : student.academicStatus === "inactive"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {student.academicStatus || "active"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleViewDetails(student._id)}
                        className="inline-flex items-center p-2 rounded-lg shadow-md hover:shadow-lg text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-all duration-200 border-0"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(student._id)}
                        className="inline-flex items-center p-2 rounded-lg shadow-md hover:shadow-lg text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 transition-all duration-200 border-0"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(student._id)}
                        className="inline-flex items-center p-2 rounded-lg shadow-md hover:shadow-lg text-red-600 hover:text-red-700 hover:bg-red-50 transition-all duration-200 border-0"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {selectedStudentId && (
        <>
          <StudentDetailsModal
            isOpen={isDetailsModalOpen}
            onClose={() => {
              setIsDetailsModalOpen(false);
              setSelectedStudentId(null);
            }}
            studentId={selectedStudentId}
            onEdit={() => {
              setIsDetailsModalOpen(false);
              setIsEditModalOpen(true);
            }}
          />
          <StudentModal
            isOpen={isEditModalOpen}
            onClose={() => {
              setIsEditModalOpen(false);
              setSelectedStudentId(null);
            }}
            studentId={selectedStudentId}
          />
        </>
      )}
    </>
  );
};

export default StudentsTable;
